/// Anchoring — the catalogue of anchor types, how each performs in
/// different holding ground, scope guidance, and when a kedge earns
/// its locker space. Reference-only data; the apps/web layer just
/// renders the cards.

// ─── Anchor types ───────────────────────────────────────────────

export type AnchorFamily =
  | "plough"
  | "fluke"
  | "claw"
  | "scoop"
  | "fisherman"
  | "mushroom"
  | "grapnel";

export interface Anchor {
  id: string;
  /// Common name(s) — most cruisers will know one of these.
  name: string;
  family: AnchorFamily;
  /// Short summary used as the card subtitle.
  summary: string;
  /// Bullet list of strengths.
  strengths: readonly string[];
  /// Bullet list of weaknesses or known failure modes.
  weaknesses: readonly string[];
  /// Typical role — primary bower, secondary kedge, dedicated storm,
  /// emergency, etc.
  role: string;
}

export const ANCHORS: Readonly<Record<string, Anchor>> = Object.freeze({
  cqr: {
    id: "cqr",
    name: "CQR (Plough)",
    family: "plough",
    summary:
      "Hinged-shank plough — the classic British cruising anchor. Resets well on a wind shift.",
    strengths: [
      "All-round performance in sand, mud, clay.",
      "Hinged shank lets the fluke pivot — re-sets cleanly when the boat swings.",
      "Stows well on a bow roller.",
    ],
    weaknesses: [
      "Slow to set — has to be dragged some distance before the fluke digs.",
      "Struggles in weed and very soft mud.",
      "Heavier than modern scoop designs for equivalent holding.",
    ],
    role: "Traditional primary bower on yachts built before the 2000s.",
  },
  delta: {
    id: "delta",
    name: "Delta",
    family: "plough",
    summary:
      "Fixed-shank plough — Lewmar's evolution of the CQR with no moving parts.",
    strengths: [
      "Sets faster than a CQR thanks to ballasted tip.",
      "Reliable in sand, mud, and most mixed bottoms.",
      "Self-launching on a bow roller.",
    ],
    weaknesses: [
      "Less effective than modern scoops in soft mud or weed.",
      "Tends to drag rather than re-set in a 180° wind shift.",
    ],
    role: "Common factory-fit primary anchor on modern production yachts.",
  },
  rocna: {
    id: "rocna",
    name: "Rocna",
    family: "scoop",
    summary:
      "Roll-bar scoop — high-aspect concave blade with a roll-bar to force fluke-down orientation.",
    strengths: [
      "Sets in one or two boat-lengths, even from upside down.",
      "Among the highest holding-power-to-weight ratios available.",
      "Excellent in sand, mud, weed, and shale.",
    ],
    weaknesses: [
      "Roll-bar can foul bow rollers not sized for it.",
      "Awkward to deck-stow without a dedicated cradle.",
      "Premium price.",
    ],
    role: "Modern primary bower — popular upgrade from CQR / Delta.",
  },
  manson_supreme: {
    id: "manson_supreme",
    name: "Manson Supreme",
    family: "scoop",
    summary:
      "Roll-bar scoop with a slotted shank — almost identical performance to Rocna.",
    strengths: [
      "Slotted shank lets the rode break out if anchor is fouled on a wreck or cable.",
      "Sets very quickly and resets reliably.",
      "Wide pressure-distribution blade for soft bottoms.",
    ],
    weaknesses: [
      "Same bow-roller compatibility caveats as Rocna.",
      "Slot can occasionally trap weed.",
    ],
    role: "Primary bower — direct alternative to Rocna.",
  },
  bruce: {
    id: "bruce",
    name: "Bruce (Claw)",
    family: "claw",
    summary:
      "Three-claw cast steel anchor — originally designed for North Sea oil-rig moorings.",
    strengths: [
      "Sets in any orientation — no risk of landing 'upside down'.",
      "Holds well in poor-visibility conditions and rocky bottoms.",
      "Robust, no moving parts.",
    ],
    weaknesses: [
      "Lower holding power per kg than a modern scoop.",
      "Awkward to stow flat — designed for a roller.",
      "Genuine Bruce production ceased — most 'Bruce-style' on the market are copies of varying quality.",
    ],
    role: "Common secondary or kedge on cruisers; sometimes the primary on smaller boats.",
  },
  danforth: {
    id: "danforth",
    name: "Danforth / Fortress (Fluke)",
    family: "fluke",
    summary:
      "Flat twin-fluke lightweight anchor — the standard kedge on the transom of every motor cruiser.",
    strengths: [
      "Outstanding holding-power-to-weight ratio in sand and mud.",
      "Stows flat — fits in a cockpit locker.",
      "Fortress aluminium versions are extremely light.",
    ],
    weaknesses: [
      "Useless on rock — the flukes can't bite.",
      "Trips easily on a 180° wind/tide shift — the flukes pull free.",
      "Aluminium versions can bend if loaded too far off-axis.",
    ],
    role: "Standard kedge anchor; tender / dinghy primary; second anchor for Bahamian moor.",
  },
  fisherman: {
    id: "fisherman",
    name: "Fisherman / Admiralty",
    family: "fisherman",
    summary:
      "Traditional shank-and-stock design with a single fluke biting and the other in the air.",
    strengths: [
      "Works in rock and heavy weed where modern anchors fail.",
      "Strong shank — won't fold under a side load.",
      "The classic 'storm anchor' for offshore cruisers.",
    ],
    weaknesses: [
      "Heavy for its holding power compared with a scoop.",
      "Awkward to stow — folds but still bulky.",
      "The non-biting fluke can foul on the rode in light winds.",
    ],
    role: "Dedicated storm or rock-bottom anchor; rarely the primary on a modern cruiser.",
  },
  mushroom: {
    id: "mushroom",
    name: "Mushroom",
    family: "mushroom",
    summary:
      "Inverted-bowl shape that beds into soft mud — used for permanent moorings.",
    strengths: [
      "Enormous holding once buried; relies on suction more than fluke action.",
      "No moving parts, no maintenance.",
    ],
    weaknesses: [
      "Holds only after weeks of settling into the seabed — useless for short-term anchoring.",
      "Mud-only; doesn't bury in sand or hard ground.",
    ],
    role: "Permanent mooring weights — not carried on board.",
  },
  grapnel: {
    id: "grapnel",
    name: "Grapnel",
    family: "grapnel",
    summary:
      "Four-pronged folding anchor — pocket-size emergency hook for tenders and kayaks.",
    strengths: [
      "Folds flat for stowage in a small cockpit locker or rucksack.",
      "Good in rocky or weedy bottoms — a fluke usually catches something.",
      "Light and cheap.",
    ],
    weaknesses: [
      "Low holding in sand or mud — flukes are too small.",
      "Can foul on its own rode in a tide reversal.",
    ],
    role: "Tender / RIB / dinghy emergency anchor.",
  },
});

export const ANCHOR_IDS: readonly string[] = Object.keys(ANCHORS);

// ─── Holding ground ─────────────────────────────────────────────

export interface HoldingGround {
  id: string;
  name: string;
  /// Chart abbreviation if any (e.g. "M" for mud).
  chartAbbreviation: string | null;
  rating: "excellent" | "good" | "moderate" | "poor" | "avoid";
  notes: string;
}

export const HOLDING_GROUNDS: Readonly<Record<string, HoldingGround>> =
  Object.freeze({
    sand: {
      id: "sand",
      name: "Sand",
      chartAbbreviation: "S",
      rating: "good",
      notes:
        "Most anchors set and hold well. Avoid very fine 'silty' sand which can scour out under load.",
    },
    mud: {
      id: "mud",
      name: "Mud",
      chartAbbreviation: "M",
      rating: "good",
      notes:
        "Fluke anchors (Danforth) hold superbly once set. Ploughs and scoops bury deeply. Very soft mud needs extra scope.",
    },
    clay: {
      id: "clay",
      name: "Clay",
      chartAbbreviation: "Cy",
      rating: "excellent",
      notes:
        "Holds anything that gets in. Can be sticky to break out at weigh-anchor time — motor up before pulling.",
    },
    shingle: {
      id: "shingle",
      name: "Shingle / gravel",
      chartAbbreviation: "G",
      rating: "moderate",
      notes:
        "Variable. Larger anchor scoops sometimes catch; flukes often slide. Try setting twice if first attempt skips.",
    },
    rock: {
      id: "rock",
      name: "Rock",
      chartAbbreviation: "R",
      rating: "poor",
      notes:
        "A Fisherman/Admiralty or grapnel can catch in cracks; a modern scoop or fluke will skid. Trip line strongly recommended in case the anchor fouls.",
    },
    weed: {
      id: "weed",
      name: "Weed (kelp, eelgrass)",
      chartAbbreviation: "Wd",
      rating: "poor",
      notes:
        "Anchor lands on top of the weed and slides. If you must anchor, choose a sand patch within the weed and motor down hard.",
    },
    coral: {
      id: "coral",
      name: "Coral",
      chartAbbreviation: "Co",
      rating: "avoid",
      notes:
        "Don't anchor in coral — environmental damage and likely a fouled anchor. Pick a sand patch or use a pre-laid mooring.",
    },
  });

export const HOLDING_GROUND_IDS: readonly string[] = Object.keys(HOLDING_GROUNDS);

// ─── Scope ──────────────────────────────────────────────────────

export interface ScopeGuideline {
  /// Type of rode setup.
  rode: "all-chain" | "chain-and-rope" | "all-rope";
  rodeLabel: string;
  /// Minimum scope (length ÷ depth) for settled conditions.
  minimum: string;
  /// Recommended scope for normal overnight conditions.
  normal: string;
  /// Heavy-weather minimum.
  heavy: string;
  notes: string;
}

export const SCOPE_GUIDELINES: readonly ScopeGuideline[] = [
  {
    rode: "all-chain",
    rodeLabel: "All chain",
    minimum: "3 : 1",
    normal: "5 : 1",
    heavy: "7 : 1 or more",
    notes:
      "Chain's weight creates a catenary curve — the angle at the anchor is far flatter than the angle at the bow. Less scope needed than rope.",
  },
  {
    rode: "chain-and-rope",
    rodeLabel: "Chain + rope warp",
    minimum: "5 : 1",
    normal: "7 : 1",
    heavy: "10 : 1 or more",
    notes:
      "The rope section gives no catenary, so more length is needed to keep the pull horizontal at the anchor. Typical setup: 10 m of chain to take the chafe + abrasion, then warp.",
  },
  {
    rode: "all-rope",
    rodeLabel: "All rope (dinghy / kedge)",
    minimum: "7 : 1",
    normal: "10 : 1",
    heavy: "Add a length of chain near the anchor and increase scope.",
    notes:
      "Pure rope is rare on cruising yachts. Always shackle 1–2 m of chain immediately above the anchor — it stops the rope chafing on the bottom and adds weight to help the anchor set.",
  },
];

/// Depth used for the scope calculation is from the bow chock to the
/// seabed at high water — not just charted depth at the moment of
/// anchoring. Forgetting tidal rise is the most common anchoring
/// mistake.
export const SCOPE_FORMULA_NOTE =
  "Scope = rode deployed ÷ depth from bow chock to seabed at high water (not just the depth right now). Add tidal range when calculating in a rising tide.";

// ─── Kedge use cases ────────────────────────────────────────────

export interface KedgeUseCase {
  id: string;
  title: string;
  /// What's happening that makes a kedge useful.
  scenario: string;
  /// How to deploy it.
  method: string;
}

export const KEDGE_USES: readonly KedgeUseCase[] = [
  {
    id: "limit-swing",
    title: "Limit swing room",
    scenario:
      "Anchored in a crowded harbour or near a hazard where the boat must not swing through 360° as the tide turns.",
    method:
      "Bahamian moor — set the main anchor into the current, drop back, run out the kedge in the opposite direction (typically with the dinghy), then haul back to halfway and snub both rodes off the bow.",
  },
  {
    id: "tide-turn",
    title: "Keep the bow off a quay",
    scenario:
      "Lying alongside in a tidal river. When the tide turns, the boat could be pushed onto the dock.",
    method:
      "Drop the kedge off the bow at slack water, run the rode aft to the cockpit, and let it hold the bow off as the tide reverses.",
  },
  {
    id: "kedge-off",
    title: "Kedge off when aground",
    scenario:
      "Touched bottom on a falling tide or in soft mud. Need to pull the boat into deeper water before the tide drops further.",
    method:
      "Row the kedge out in the dinghy in the direction of deeper water, drop, return to the boat, take up on a winch or windlass. Heel the boat by sending crew up the shrouds (sail) or moving weight (power) to reduce draft while pulling.",
  },
  {
    id: "storm-tandem",
    title: "Heavy-weather tandem",
    scenario:
      "Forecast for severe weather while anchored in an exposed roadstead. One anchor alone might drag.",
    method:
      "Run a second anchor in tandem — kedge shackled into the chain about 5 m above the main anchor — so both bite together. Or set two anchors in a 30–45° V from the bow for redundancy.",
  },
  {
    id: "stern-anchor",
    title: "Stern anchor for med-mooring",
    scenario:
      "Med-style mooring (stern-to a quay with the bow held off by an anchor) on a coast without lazy lines.",
    method:
      "Drop the bow anchor (sometimes the main bower) several boat lengths out, reverse to the quay paying out rode, secure stern lines, then take up on the bow anchor to hold the boat off the wall.",
  },
];

// ─── Procedure summary ──────────────────────────────────────────

/// Step-by-step procedure for setting an anchor. The Knots page
/// uses a similar format; we mirror it here so the look-up reads
/// consistently across modules.
export interface ProcedureStep {
  text: string;
}

export const SETTING_PROCEDURE: readonly ProcedureStep[] = [
  {
    text: "Survey the anchorage on the chart — depth, range, holding ground, swing room, exit route, lee shore.",
  },
  {
    text: "Calculate scope needed using depth at HIGH WATER + freeboard to bow chock. Make sure that length of rode is available.",
  },
  {
    text: "Brief the crew — who's on the helm, who's on the bow, who watches for transits.",
  },
  {
    text: "Approach into the wind (or tide if stronger) at low speed. Stop the boat over the chosen drop point.",
  },
  {
    text: "Lower the anchor — don't throw it. Keep it just clear of the bottom until the boat starts to drift back, then pay out rode as you drop back.",
  },
  {
    text: "Let out the planned scope; cleat or snub the rode.",
  },
  {
    text: "Apply engine astern at idle — feel the anchor bite. If it drags, increase scope or weigh and try a new spot.",
  },
  {
    text: "Take a fix or two transit bearings to monitor for dragging. Check again at every watch change overnight.",
  },
];

export const WEIGHING_PROCEDURE: readonly ProcedureStep[] = [
  {
    text: "Start the engine. Brief the crew — helm + bow signals.",
  },
  {
    text: "Motor gently toward the anchor, bow crew taking in slack chain as it accumulates.",
  },
  {
    text: "When the chain is up-and-down (vertical), the anchor should break out. Don't try to lift it with the windlass before it's clear of the bottom.",
  },
  {
    text: "Hose mud / weed off the anchor before stowing. Secure it in the bow roller or on deck.",
  },
  {
    text: "If the anchor is fouled (won't break out), motor slowly in a circle to twist it free, or use a tripping line if rigged.",
  },
];
